import { getCustomerIssueTask } from '../utils/customerTaskRouting';
import { getCustomerTaskNeededFor } from './CustomerTaskLayout';

import type { ReactNode } from 'react';
import type { CustomerTaskDefinition, CustomerTaskIssueDefinition } from '../types/customerTask';

export interface CustomerIssueTaskRenderProps<
  TTask extends CustomerTaskDefinition = CustomerTaskDefinition,
  TIssueTask extends CustomerTaskDefinition = TTask,
> {
  effectiveDate?: string;
  issue: CustomerTaskIssueDefinition<TIssueTask>;
  neededFor: string;
  task: TTask;
}

export interface CustomerIssueTaskRenderers<TTask extends CustomerTaskDefinition> {
  info: (props: CustomerIssueTaskRenderProps<Extract<TTask, { taskType: 'info' }>, TTask>) => ReactNode;
  input: (props: CustomerIssueTaskRenderProps<Extract<TTask, { taskType: 'put_customer_info' }>, TTask>) => ReactNode;
  link: (props: CustomerIssueTaskRenderProps<Extract<TTask, { taskType: 'complete_link' }>, TTask>) => ReactNode;
  multiStep: (props: Omit<CustomerIssueTaskRenderProps<TTask, TTask>, 'task'>) => ReactNode;
  select: (props: CustomerIssueTaskRenderProps<Extract<TTask, { taskType: 'select' }>, TTask>) => ReactNode;
  freeTextResponse: (props: CustomerIssueTaskRenderProps<Extract<TTask, { taskType: 'free_text_response' }>, TTask>) => ReactNode;
}

interface CustomerIssueTaskProps<TTask extends CustomerTaskDefinition> {
  issue: CustomerTaskIssueDefinition<TTask>;
  renderers: CustomerIssueTaskRenderers<TTask>;
}

function isTaskType<TTask extends CustomerTaskDefinition, TType extends TTask['taskType']>(
  task: TTask,
  taskType: TType
): task is Extract<TTask, { taskType: TType }> {
  return task.taskType === taskType;
}

/** Shared customer-issue routing. Apps inject side-effectful interactive task adapters. */
export function CustomerIssueTask<TTask extends CustomerTaskDefinition>({
  issue,
  renderers,
}: CustomerIssueTaskProps<TTask>) {
  const neededFor = getCustomerTaskNeededFor(issue.forEndorsements, '');
  const commonProps = { effectiveDate: issue.effectiveDate, issue, neededFor };

  if (issue.tasks && issue.tasks.length > 1) {
    return renderers.multiStep(commonProps);
  }

  const task = getCustomerIssueTask(issue);
  if (!task) return null;

  if (isTaskType(task, 'info')) return renderers.info({ ...commonProps, task });
  if (isTaskType(task, 'complete_link')) return renderers.link({ ...commonProps, task });
  if (isTaskType(task, 'put_customer_info')) return renderers.input({ ...commonProps, task });
  if (isTaskType(task, 'select')) return renderers.select({ ...commonProps, task });
  if (isTaskType(task, 'free_text_response')) return renderers.freeTextResponse({ ...commonProps, task });
  return null;
}
